import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import apiAxios from "helpers/apiAxios";
import { objectSerializer } from "helpers/utils";
import { toast } from "react-toastify";

const applicationUrl = "/integration/applications";

export const getApplications = createAsyncThunk(
  "Application/getApplications",
  async (params, {rejectWithValue}) => {
    try {
      const response = await apiAxios.get(`${applicationUrl}`, { params } )
      return response.data
    }
    catch(error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      } else {
        return rejectWithValue(error.message);
      }
    }
  }
)

export const getApplication = createAsyncThunk(
  "Application/getApplication",
  async (applicationId, { rejectWithValue }) => {
    try {
      const response = await apiAxios.get(`${applicationUrl}/${applicationId}`)
      return response.data
    }
    catch(error) {
      return rejectWithValue(error.response.data)
    }
  }
)

export const createApplication = createAsyncThunk(
  "Application/createApplication",
  async (data, { rejectWithValue }) => {
    try {
      const response = await apiAxios.post(applicationUrl, data)
      return response.data
    }
    catch(error){
      return rejectWithValue(error.response.data)
    }
  }
)

export const updateApplication = createAsyncThunk(
  "Application/updateApplication",
  async ({ applicationId, data }, { rejectWithValue }) => {
    try {
      const response = await apiAxios.put(`${applicationUrl}/${applicationId}`, data)
      return response.data
    }
    catch(error) {
      return rejectWithValue(error.response.data);
    }
  }
)

// Whose token this client may ask for. Its own endpoint rather than a field on
// updateApplication: widening a client to "any user" is a different decision
// from renaming it, and the API audits it as one.
export const updateImpersonation = createAsyncThunk(
  "Application/updateImpersonation",
  async ({ applicationId, impersonationMode, impersonatableEmails }, { rejectWithValue }) => {
    try {
      const response = await apiAxios.patch(`${applicationUrl}/${applicationId}/impersonation`, {
        impersonation_mode: impersonationMode,
        impersonatable_emails: impersonatableEmails
      })
      return response.data
    }
    catch(error) {
      return rejectWithValue(error.response?.data || { error: error.message })
    }
  }
)

export const deleteApplication = createAsyncThunk(
  "Application/deleteApplication",
  async (applicationId) => {
    try {
      const response = await apiAxios.delete(`${applicationUrl}/${applicationId}`);
      return response.data
      
    } catch (error) {
      throw new Error(error.message)
    }
  }
)


const isRejectedAction = (action) => (action.type.startsWith("Application/")) && action.type.endsWith("/rejected");

const handlePayload = (state, action, type="none") => {
  const { error, data, message } = action.payload || {};

  if (error) {
    toast.error(error);
  } else {
    const record = data && data.attributes ? data.attributes : null;
    
    if (record !== null) {
      state.applicationData = record

      switch (type) {
        case "create": 
          state.data.unshift(record)
          break
        case "delete":
          state.data = state.data.filter((item) => item.id !== record.id)
          break;
        default:
        {
          const updatedIndex = state.data.findIndex((item) => item.id === record.id)
          if (updatedIndex !== -1) {
            state.data[updatedIndex] = record
          }

          break;
        }
      }
    }
    toast.success(message);
  }
};

const applicationSlice = createSlice({
  name: "integration_application",
  initialState: {
    data: [],
    totalRows: 0,
    loading: false,
    exporting: false,
    savingImpersonation: false,
    applicationData: {}
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getApplications.pending, (state) => {
        state.loading = true
      })
      .addCase(getApplications.fulfilled, (state, action) => {
        state.loading = false
        const results = action.payload

        state.data      = objectSerializer(results.rows)
        state.totalRows = results.total_rows
      })
      // The detail fetch has to move `loading` too: the screen shows a client's
      // secret and an editable impersonation policy, so rendering the previously
      // opened client while the next one is in flight is not a cosmetic glitch —
      // Save would write to whatever record is still in the store.
      .addCase(getApplication.pending, (state) => {
        state.loading = true
      })
      .addCase(getApplication.fulfilled, (state, action) => {
        state.loading = false
        handlePayload(state, action)
      })
      .addCase(createApplication.fulfilled, (state, action) => {
        handlePayload(state, action, "create")
      })
      .addCase(updateApplication.fulfilled, (state, action) => {
        handlePayload(state, action)
      })
      .addCase(updateImpersonation.pending, (state) => {
        state.savingImpersonation = true
      })
      .addCase(updateImpersonation.fulfilled, (state, action) => {
        state.savingImpersonation = false
        handlePayload(state, action)
      })
      .addCase(updateImpersonation.rejected, (state) => {
        state.savingImpersonation = false
      })
      .addCase(deleteApplication.fulfilled, (state, action) => {
        handlePayload(state, action, "delete")
      })
      .addMatcher(
        isRejectedAction,
        (state, action) => {
          state.loading = false;
          const error = action.payload?.message || action.payload?.error || action.error.message;
          if (error.includes(401)) {
            toast.error('Unauthorized access. Please contact IT to support.');
          } else {
            toast.error(error);
          }
        }
      );
  }
});

//export const {} = applicationSlice.actions;

export default applicationSlice.reducer;